'use client';

/**
 * **When a Radix layer's "that was outside me" verdict carries no information**
 * (VALE-1652).
 *
 * `DismissableLayer` does not decide *outside* from the DOM. It decides it from
 * React: the layer puts an `onPointerDownCapture` on its own element, React's
 * synthetic dispatch walks the React tree — through portals, which is the whole
 * point — and a layer that hears that handler fire knows the pointer went down
 * somewhere inside it. A layer that does NOT hear it concludes the interaction
 * was outside, and dismisses.
 *
 * That inference is sound only while React actually dispatches. **React will
 * not dispatch to an unmounted fiber**: if the element the pointer went down on
 * is removed from the tree before the synthetic dispatch runs, React abandons
 * the entire propagation path, and not one `onPointerDownCapture` between the
 * target and the root is called. Every layer above the removed element then
 * hears silence and reads it as *outside* — when the truth is that the
 * interaction was inside, and something reacted to it so promptly that the
 * evidence was gone before React looked.
 *
 * That is not hypothetical, and it is not rare. The attribute treatment's
 * composite popover editors commit from a **capture-phase `document`
 * pointerdown listener** ({@link useAttributePopoverCommit}, VALE-1617, which
 * has to run that early for reasons of its own). Committing closes the editor,
 * closing it re-renders the cell, and the re-render replaces the read-out
 * button the pointer just went down on — synchronously, because React flushes
 * discrete updates. So on a review dialog hosting this treatment, the click
 * that dismisses an inline editor is the click the dialog mistakes for a click
 * on the page behind it. With an unsaved-changes guard on that dialog, the
 * reviewer is asked to discard work they never asked to leave.
 *
 * ## Why the test is the target's connectedness
 *
 * A genuine outside interaction — the overlay, the page behind the dialog, a
 * `Cancel` button, the close control — targets an element that is still in the
 * document at the moment the verdict is reached. Nothing has had the chance to
 * remove it: the dismissal is dispatched during the very gesture that produced
 * it, before any `click` handler has run. **A detached target means the page
 * mutated itself mid-gesture**, which is exactly the condition under which the
 * verdict was reached without evidence.
 *
 * The failure is one-sided and cheap in the wrong direction: a suppressed
 * dismissal costs the reader one extra click, while an honoured one destroys
 * work. So this is deliberately a *narrow* suppression and not a broad
 * "ignore interactions I am not sure about".
 *
 * ## Scope
 *
 * This is a predicate, not a policy — a host decides what to do with the
 * answer, and pays for it with one line in its own `onInteractOutside`:
 *
 * ```tsx
 * onInteractOutside={(event) => {
 *   if (isUnattributedOutsideInteraction(event)) event.preventDefault();
 * }}
 * ```
 *
 * It lives beside the treatment that causes the condition rather than beside
 * the dialogs that suffer it, so the reasoning has one home and a second host
 * adopting it binds to this rather than re-deriving the test.
 */

/**
 * The shape Radix hands `onInteractOutside` / `onPointerDownOutside`, narrowed
 * to the one field this reads. Declared structurally rather than imported so
 * the predicate is usable from a `Dialog`, a `Sheet` or a `Popover` alike,
 * none of which share a props type.
 */
export interface OutsideInteractionEvent {
  detail: { originalEvent: Event };
}

/**
 * Whether this outside-interaction verdict was reached with **no React
 * attribution at all**, because the event's own target had already been removed
 * from the document — see this module's header for why that makes the verdict
 * meaningless rather than merely doubtful.
 *
 * `true` means *do not dismiss on this one*.
 */
export function isUnattributedOutsideInteraction(
  event: OutsideInteractionEvent
): boolean {
  const target = event.detail?.originalEvent?.target;
  return target instanceof Node && !target.isConnected;
}
